import { ATTRIBUTION_KEYS } from 'constants/attribution';
import { OAUTH_SECURITY_TOKEN_KEY } from 'constants/oauth';
import type { UTM } from 'stores/zustand/attributionStore';

import config from './config';
import logProviderFactory from './logger/logProviderFactory';
import { getLocalStorageWithExpiration, setLocalStorageWithExpiration } from './storage';

export enum OAuthProvider {
    GOOGLE = 'google',
    APPLE = 'apple',
}
export type OAuthProviderType = OAuthProvider.GOOGLE | OAuthProvider.APPLE;

export interface OAuthState {
    request_mfa: string;
    security_token: string;
    utm_source: string | undefined;
    utm_term: string | undefined;
    utm_landing: string | undefined;
    utm_campaign: string | undefined;
    utm_medium: string | undefined;
    utm_content: string | undefined;
    referral_code: string | undefined;
    is_world_app: boolean;
    redirect: string | undefined;
    gclid: string | undefined;
    fbclid: string | undefined;
    msclkid: string | undefined;
    dclid: string | undefined;
    rdt_cid: string | undefined;
    ttclid: string | undefined;
    twclid: string | undefined;
    sc_cid: string | undefined;
    source: string | undefined;
    initial_referrer: string | undefined;
}

declare const window: any;

const providers = {
    google: {
        type: OAuthProvider.GOOGLE,
        clientId: config.googleOAuthClientId,
        redirectUri: '/oauth-web/google/callback',
        authorizationUrl: 'https://accounts.google.com/o/oauth2/auth',
        scope: 'openid profile email',
        response_mode: '',
    },
    apple: {
        type: OAuthProvider.APPLE,
        clientId: config.appleOAuthClientId,
        redirectUri: '/api/oauth-web/apple',
        authorizationUrl: 'https://appleid.apple.com/auth/authorize',
        scope: 'email',
        response_mode: 'form_post',
    },
};

export const buildOAuthState = (
    securityToken: string,
    utmData: UTM,
    requestMfa?: boolean,
    referralCode?: string,
    redirect?: string,
): OAuthState => {
    const gclid = getLocalStorageWithExpiration(ATTRIBUTION_KEYS.GCLID) || undefined;
    const fbclid = getLocalStorageWithExpiration(ATTRIBUTION_KEYS.FBCLID) || undefined;
    const msclkid = getLocalStorageWithExpiration(ATTRIBUTION_KEYS.MSCLKID) || undefined;
    const dclid = getLocalStorageWithExpiration(ATTRIBUTION_KEYS.DCLID) || undefined;
    const rdt_cid = getLocalStorageWithExpiration(ATTRIBUTION_KEYS.RDT_CID) || undefined;
    const ttclid = getLocalStorageWithExpiration(ATTRIBUTION_KEYS.TTCLID) || undefined;
    const twclid = getLocalStorageWithExpiration(ATTRIBUTION_KEYS.TWCLID) || undefined;
    const sc_cid = getLocalStorageWithExpiration(ATTRIBUTION_KEYS.SC_CID) || undefined;
    const source = getLocalStorageWithExpiration(ATTRIBUTION_KEYS.SOURCE) || undefined;
    let initial_referrer: string | undefined;
    if (typeof sessionStorage !== 'undefined') {
        // Truncate to avoid exceeding OAuth state URL length limits.
        // Note: real-time touchpoints (via sessionStorage) send the full referrer.
        const raw = sessionStorage.getItem(ATTRIBUTION_KEYS.INITIAL_REFERRER);
        initial_referrer = raw ? raw.slice(0, 256) : undefined;
    }

    return {
        request_mfa: requestMfa ? 'true' : 'false',
        security_token: securityToken,
        utm_source: utmData.utm_source,
        utm_term: utmData.utm_term,
        utm_landing: utmData.utm_landing,
        utm_campaign: utmData.utm_campaign,
        utm_medium: utmData.utm_medium,
        utm_content: utmData.utm_content,
        referral_code: referralCode,
        is_world_app: !!window.WorldApp,
        redirect,
        gclid,
        fbclid,
        msclkid,
        dclid,
        rdt_cid,
        ttclid,
        twclid,
        sc_cid,
        source,
        initial_referrer,
    };
};

export const generateSecurityToken = (): string => {
    // Security token to prevent CSRF attacks. Randomize and check on the callback.
    const oAuthSecurityToken =
        Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

    setLocalStorageWithExpiration(OAUTH_SECURITY_TOKEN_KEY, oAuthSecurityToken);
    return oAuthSecurityToken;
};

export const getRedirectUri = (provider: OAuthProvider): string => {
    const { redirectUri } = providers[provider];
    return `${window.origin}${redirectUri}`;
};

function buildOAuthAuthorizationUrl(
    provider: OAuthProvider,
    utmData: UTM,
    requestMfa?: boolean,
    referralCode?: string,
    redirect?: string,
): string {
    const { clientId, redirectUri, authorizationUrl, scope, response_mode } = providers[provider];

    const securityToken = generateSecurityToken();
    const state = buildOAuthState(securityToken, utmData, requestMfa, referralCode, redirect);

    const params = new URLSearchParams({
        client_id: clientId,
        redirect_uri: `${window.origin}${redirectUri}`,
        response_type: 'code',
        scope,
        state: JSON.stringify(state),
    });

    if (provider === OAuthProvider.APPLE) {
        params.append('response_mode', response_mode);
    }

    if (provider === OAuthProvider.GOOGLE) {
        params.append('prompt', 'select_account');
    }

    return `${authorizationUrl}?${params.toString()}`;
}

export const handleOAuthLogin = (
    provider: OAuthProvider,
    utmData: UTM,
    requestMfa?: boolean,
    referralCode?: string,
    onRedirect?: () => void,
    redirect?: string,
) => {
    try {
        const authUrl = buildOAuthAuthorizationUrl(provider, utmData, requestMfa, referralCode, redirect);

        window.location.href = authUrl;
        onRedirect?.();
    } catch (e) {
        logProviderFactory.logError(`Handle oAuth login error - ${e}`);
        window.location.href = '/sign-in';
    }
};
